import React, { Fragment, useState, useRef, useEffect } from "react";
import axios from 'axios';
import Helpers, { ApiHelper, DocumentHelper, triggerSubmit } from '../../helpers';
import Config from '../../config';
import Address, { updateAddress } from '../../components/address'
import {Card, CardContent, Grid as GridBase, Table, TableBody, TableCell, TableHead, TableRow, Toolbar, Button, MenuItem, Dialog, DialogContent, Typography, DialogActions, RadioGroup, FormControlLabel, Radio} from '@mui/material';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import TextFieldBase from '../../components/text_field';
import Auth from '../../auth';
import DocumentTables from '../../components/document_tables';
import DocumentToolbar from '../../components/document_toolbar';
import { Link } from 'react-router-dom';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { HeaderAttributes, ItemAttributes, LabelDialog, MoreActions } from '../documents/document_components';
import ContentLoader from '../../components/content_loader';
import { ErrorOutline as LoadErrorIcon } from '@mui/icons-material';
import AutoCompleteBase from '../../components/suggest_field';
import DownloadButton from '../../components/download_button';
import ConfirmDialog from "../../components/confirm_dialog";
import Status from '../../components/status';
import moment from 'moment';
import createDecorator from 'final-form-calculate';
import { getIn } from "final-form";
import type { DocumentDetailsProps, DocumentDetailsState, DocumentFormProps } from '../documents/types';

// Relax MUI/untyped legacy components to `any` so their custom/v7 props
// type-check (behavior unchanged). Matches documents/ack_details.tsx.
const Grid: any = GridBase;
const Form: any = FormBase;
const Field: any = FieldBase;
const TextField: any = TextFieldBase;
const AutoComplete: any = AutoCompleteBase;

const OrderDetails = (props: DocumentDetailsProps) => {
    const [state, setStateRaw] = useState<DocumentDetailsState>({order: {}, showPackingSlip: false, showError: false, showRedact: false, selectedAddress: null, showSkuMatch: false, skusToMatch: [], showReroute: false,
    orderRouting: {}, editable: false, showConfirm: false});
    // `this.state` on the class merged partial updates; mirror that with a
    // functional updater so callers can keep passing partial state slices.
    const setState = (partial: Partial<DocumentDetailsState>) => setStateRaw(prev => ({...prev, ...partial}));
    // Always-current view of state for async methods that read `this.state`
    // after awaits (the class read the live instance field).
    const stateRef = useRef(state);
    stateRef.current = state;
    // Replaces the `this.form` instance field grabbed via the child's setForm.
    const formRef = useRef<any>(null);

    const preprocess = (order: any) => {
        order.subtotal = (order.OrderLines || []).reduce((sum, value) => sum + Number((value.Price || 0) * (value.Quantity || 0)), 0);
        order.taxTotal = (order.Taxes || []).reduce((sum, value) => sum + Number(value.TaxAmount || 0), 0);
        order.discountTotal = (order.Discounts || []).reduce((sum, value) => sum + Number(value.DiscountAmount || 0), 0);
        order.ERPKey = ((order.ExtendedAttributes || []).find(r => r.Name === 'ErpKey') || {}).Value;
        if(order.RequestedShipDate){
            order.RequestedShipDate = moment(order.RequestedShipDate).format("YYYY-MM-DD");
        }
        if(order.ExpectedDeliveryDate){
            order.ExpectedDeliveryDate = moment(order.ExpectedDeliveryDate).format("YYYY-MM-DD");
        }
        (order.OrderLines || []).forEach((item, index) => {
            item.quantityShipped = ((item.ExtendedAttributes || []).find(r => r.Name === 'OrderQuantityShipped') || {}).Value || 0;
            item.quantityCancelled = ((item.ExtendedAttributes || []).find(r => r.Name === 'QtyCancelled') || {}).Value || 0;
            if(!item.LineNumber){
                item.LineNumber = (index + 1).toString();
            }
        });
    };

    const getDistinctAddresses = (order: any) => {
        var addresses: any[] = [];
        var contacts = [order.BillToAddress, order.ShipToAddress, order.OrderedByAddress];
        for(var i = 0; i < contacts.length; i++){
            var contact = contacts[i];
            var addressName = null;
            var name = null;
            if(contact && contact.FirstName && contact.LastName){
                addressName = contact.FirstName + " " + contact.LastName;
                name = {firstName: contact.FirstName, lastName: contact.LastName};
            }else if(contact && contact.CompanyName && contact.CompanyName.split(' ').length === 2){
                addressName = contact.CompanyName;
                name = {firstName: contact.CompanyName.split(' ')[0], lastName: contact.CompanyName.split(' ')[1]};
            }
            if(addressName && name){
                if(addresses.map(r => r.label).indexOf(addressName) === -1){
                    addresses.push({label: addressName, value: name});
                }
            }
        }
        return addresses;
    };

    const canReroute = (order: any) => {
        return order.SenderCompanyId === order.ReceiverCompanyId
        && props.isRetailer
        && (order.OrderLines || []).length > 0
        && order.OrderLines.every(r => (r.ExtendedAttributes || []).filter(x => x.Name === 'PO_Id').length > 0)
        && order.OrderLines.every(r => (r.ExtendedAttributes || []).filter(x => x.Name === 'PrimaryVendor').length > 0);
    };

    const load = async () => {
        let orderid = new Helpers().queryString()['orderid'];
        let order: any = await axios.get(Config.api + "/api/v1/orders/" + orderid).catch(() => setState({loadError: true}));
        if(!order){
            return;
        }
        order = order.data.Body.SalesOrder;
        props.setTitle('Order ' + order.PartnerPO, true);
        preprocess(order);
        let metadata = (await axios.get(Config.api + '/odata/Company/DocumentMetadata?$filter=Id eq ' + orderid)).data.value[0];
        let addresses = getDistinctAddresses(order);
        let reroutable = canReroute(order);
        let editable = (metadata || {}).Status === 1200 && props.isBillable;
        setState({doc: order, metadata, loaded: true, id: orderid, addresses: addresses, selectedAddress: addresses.length > 0 ? addresses[0].label : undefined, submitting: false, canReroute: reroutable, editable});
        let testCaseSummary = await new ApiHelper().getTestCaseSummary(order);
        setState({testCaseSummary});
    };

    const getSkuDetails = async (order: any, skuEvent: any) => {
        var skuDetails: any[] = [];
        var evtData = JSON.parse((await axios.get(Config.api + `/api/v1/activityevents/${skuEvent.Id}`)).data.Body.AdditionalData);
        var field = "SupplierSKU";
        if(evtData.Field){
            field = evtData.Field;
        }
        for(var i = 0; i < order.OrderLines.length; i++){
            var line = order.OrderLines[i];
            var skuToMap = line.ItemIdentifier[field];
            var details: any = {LineNumber: line.LineNumber, MerchantSKU: skuToMap};
            var partnerId = props.isRetailer ? order.ReceiverCompanyId : order.SenderCompanyId;
            try {
                let skuMatch = (await axios.get(Config.api + `/api/v1/inventory/${partnerId}/match?sku=${skuToMap}`)).data;
                details.SupplierSKU = skuMatch.Body.SupplierSKU;
                details.Cost = skuMatch.Body.Cost;
                details.PartnerId = props.isRetailer ? skuMatch.Body.SupplierId : skuMatch.Body.MerchantId;
                details.matched = true;
            } catch(e) {
                details.addItem = true;
                if(order.SenderCompanyId !== order.ReceiverCompanyId){
                    details.PartnerId = partnerId;
                    details.disablePartner = true;
                }
            }
            skuDetails.push(details);
        }
        return skuDetails;
    };

    const showSkuMatchDialog = async () => {
        let partners = (await axios.get(Config.api + `/api/v1/partners`)).data.Body.Partners;
        let options = partners.map(r => {return {value: r.Id, label: r.CompanyName}}).sort((a, b) => a.label.localeCompare(b.label));
        let skuDetails = await getSkuDetails(stateRef.current.doc, stateRef.current.mostRecentEvent);
        setState({skusToMatch: {items: skuDetails}, skuPartners: options, showSkuMatch: true, disableSkuMatch: false});
    };

    const showRerouteDialog = async () => {
        let partners = (await axios.get(Config.api + `/api/v1/partners`)).data.Body.Partners;
        let options = partners.map(r => {return {value: r.Id, label: r.CompanyName}}).sort((a, b) => a.label.localeCompare(b.label));
        let pos = (await axios.get(Config.api + `/odata/Company/Functions.DocumentSearch?$select=id,statusDescription,status,partnerCoId,linkKey,partnerCompanyName,partnerPo&$filter=documentType eq 'Order' and sourceKey eq '${stateRef.current.metadata.SourceKey}'`)).data.value;
        let items = [...stateRef.current.doc.OrderLines];
        items.forEach(item => {
            let poId = (item.ExtendedAttributes.find(r => r.Name === "PO_Id") || {}).Value;
            let po = pos.find(r => r.id === poId) || {};
            item.NewPartnerId = "";
            item.po = po;
        });
        setState({orderRouting: {items: items}, skuPartners: options, showReroute: true, disableReroute: false});
    };

    const saveSkuMatches = async (e: any) => {
        setState({disableSkuMatch: true});
        var newMatches = e.items.filter(r => !r.matched);
        var partners = newMatches.map(r => r.PartnerId.toString());
        partners = partners.filter((r, i) => partners.indexOf(r) === i);
        for(let i = 0; i < newMatches.length; i++){
            let item = newMatches[i];
            let apiItem = {MerchantSKU: item.MerchantSKU, SupplierSKU: item.SupplierSKU, Cost: item.Cost};
            if(item.addItem){
                await axios.post(Config.api + `/api/v1/inventory/${item.PartnerId}/item`, apiItem);
            } else {
                await axios.put(Config.api + `/api/v1/inventory/${item.PartnerId}/item`, apiItem);
            }
        }
        await axios.put(Config.api + `/api/v1/orders/${stateRef.current.doc.Identifier.LogicbrokerKey}/status/0`);
        setState({showSkuMatch: false});
    };

    const rerouteOrder = async (e: any) => {
        setState({disableReroute: true});
        try{
            let acks: Record<string, any> = {};
            let reroutedItems = e.items.filter(r => !(!r.NewPartnerId) && r.NewPartnerId !== r.po.partnerCoId);
            reroutedItems.forEach(item => {
                item.ExtendedAttributes = item.ExtendedAttributes.filter(r => r.Name !== "PO_Id");
                let primaryVendor = item.ExtendedAttributes.find(r => r.Name === "PrimaryVendor");
                primaryVendor.Value = item.NewPartnerId.toString();
                if(item.po.status !== 1100 && item.po.status !== 1400){
                    let ack = acks[item.po.id];
                    if(!ack){
                        ack = {
                            PartnerPO: item.po.partnerPo,
                            AcknowledgementNumber: "ACK_" + item.po.partnerPo,
                            AckLines: [],
                            Identifier: {LinkKey: item.po.linkKey},
                            SenderCompanyId: stateRef.current.doc.SenderCompanyId,
                            ReceiverCompanyId: item.po.partnerCoId
                        };
                        acks[item.po.id] = ack;
                    }
                    let ackLine = {...item, QuantityCancelled: item.Quantity};
                    delete ackLine.Quantity;
                    ack.AckLines.push(ackLine);
                }
            });
            let poIds = Object.keys(acks);
            for(let i = 0; i < poIds.length; i++){
                let ack = acks[poIds[i]];
                await axios.post(Config.api + '/api/v1/acknowledgements/request', ack, {headers: {SourceSystem: "Portal"}});
            }
            let order = {...stateRef.current.doc, OrderLines: [...e.items]};
            await axios.put(Config.api + `/api/v1/orders/${stateRef.current.doc.Identifier.LogicbrokerKey}`, order);
            await axios.put(Config.api + `/api/v1/orders/${stateRef.current.doc.Identifier.LogicbrokerKey}/status/0`);
            setState({showReroute: false});
        }catch(e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setState({disableReroute: false, showError: true, error: error});
        }
        setState({showReroute: false});
    };

    const removePersonalData = async () => {
        var selected = stateRef.current.addresses.find(r => r.label === stateRef.current.selectedAddress);
        var {value: {firstName, lastName}} = selected;
        setState({disableRedact: true});
        try{
            await axios.get(Config.api + `/odata/Company/Functions.RedactPersonalData?id=${stateRef.current.metadata.Id}&firstName=${firstName}&lastName=${lastName}`);
            await load();
            setState({showRedact: false});
        }catch(e: any){
            var error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setState({showError: true, error: error});
        }
    };

    const saveOrder = async (values: any) => {
        setState({submitting: true, doc: values, showConfirm: false});
        try{
            await axios.put(Config.api + '/api/v1/orders/' + stateRef.current.metadata.Id, values);
            if(stateRef.current.metadata.Status === 1200){
                await axios.put(Config.api + '/api/v1/orders/' + stateRef.current.metadata.Id + '/status/0');
            }
            await load();
        }catch(e: any){
            let error = 'An unexpected error occurred.';
            if(e.response && e.response.data){
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            setState({showError: true, error: error, submitting: false});
        }
    };

    const handleSubmit = async (values: any) => {
        if(stateRef.current.metadata.Status === 1200){
            await saveOrder(values);
            return;
        }
        let message = <span>These updates will be saved. If you want to have the order resent to the supplier with the changes made, click on <b>More Actions</b> {'>'} <b>Change Status</b> {'>'} <b>New</b>. Please note this may create a new order in the supplier's system. Do you want to continue?</span>;
        if (stateRef.current.metadata.Status === 1000){
            message = <span>These updates will only apply to the order document as it is in a <b>Complete</b> status and will not be reflected on any related documents including shipments or invoices. Do you want to continue?</span>;
        }
        setState({showConfirm: true, handleConfirm: () => saveOrder(values), confirmMessage: message});
    };

    // `signalrStatusUpdate` was `.bind(this)` on the class; it reaches back into
    // `this.state`/`this.setState`/`this.preprocess`. Bind it once to a stable
    // proxy object kept in sync with the latest state so the same handler
    // reference can be registered and later removed from the SignalR connection.
    const selfRef = useRef<any>({});
    selfRef.current.state = state;
    selfRef.current.setState = setState;
    selfRef.current.preprocess = preprocess;
    const handlerRef = useRef<any>(null);
    if(handlerRef.current === null){
        handlerRef.current = new DocumentHelper().signalrStatusUpdate.bind(selfRef.current);
    }
    const handler = handlerRef.current;

    // componentDidMount: initial load.
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidMount / componentDidUpdate / componentWillUnmount: keep the
    // SignalR 'notify' subscription tied to the current connection.
    useEffect(() => {
        if(props.signalr){
            props.signalr.on('notify', handler);
            return () => props.signalr.off('notify', handler);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.signalr]);

    // componentDidUpdate: reload when the order id in the query string changes.
    const firstUpdate = useRef(true);
    useEffect(() => {
        if(firstUpdate.current){
            firstUpdate.current = false;
            return;
        }
        var orderid = new Helpers().queryString()['orderid'];
        if(orderid !== stateRef.current.id){
            setState({id: orderid, loaded: false, loadError: false, editable: false, showConfirm: false});
            load();
        }
    });

    var metadata = state.metadata;
    if(state.loadError){
        return <Grid container spacing={2}>
            <Grid size={12}>
              <LoadErrorIcon style={{fontSize: '14rem', color: '#999'}}/>
            </Grid>
            <Grid size={12} style={{fontSize: '1.5rem', color: '#999'}}>{'Sorry, we could not load that order for you.'}</Grid>
      </Grid>;
    }
    if(!state.loaded){
        return <Grid container spacing={2}>
            <Grid size={12}>
              <ContentLoader style={{height: '90px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={12}>
              <ContentLoader style={{height: '130px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <ContentLoader style={{height: '260px', width: '100%'}}/>
            </Grid>
            <Grid size={12}>
              <ContentLoader style={{height: '60px', width: '100%'}}/>
            </Grid>
            <Grid size={12}>
              <ContentLoader style={{height: '60px', width: '100%'}}/>
            </Grid>
            <Grid size={12}>
              <ContentLoader style={{height: '60px', width: '100%'}}/>
            </Grid>
            <Grid size={12}>
              <ContentLoader style={{height: '60px', width: '100%'}}/>
            </Grid>
        </Grid>
    }
    return (
        <div>
            {state.loaded ?
            <>
                <Grid container spacing={2}>
                <Grid size={12}>
                    <DocumentToolbar
                    metadata={state.metadata}
                    mostRecentEvent={state.mostRecentEvent}
                    mostRecentPost={state.mostRecentPost}
                    testCaseSummary={state.testCaseSummary}
                    title={`Order ${metadata.PartnerPo} (ID# ${metadata.Id}) (${metadata.CoId.toString() === Auth.getAccountNumber() ? metadata.PartnerCompanyName : metadata.CompanyName})`}>
                        <MoreActions isBillable={props.isBillable} metadata={metadata} onStatusChange={(metadata) => setState({metadata: metadata})} ediExists={state.ediExists}>
                                    {metadata.Status >= 1000 && state.addresses && state.addresses.length > 0 && <MenuItem onClick={() => setState({disableRedact: false, showRedact: true})}>Remove Personal Data</MenuItem>}
                                    {state.canReroute && <MenuItem onClick={showRerouteDialog}>Reroute Items</MenuItem>}
                                    <MenuItem component={Link} to={`/order-management/return-details?orderid=${metadata.Id}`}>Return Items</MenuItem>
                                    <MenuItem onClick={() => setState({showPackingSlip: true})}>View Packing Slip</MenuItem>
                                    {metadata.Status === 1200 && props.isBillable && state.mostRecentEvent && state.mostRecentEvent.EventTypeId === 6 && state.mostRecentEvent.Summary.indexOf("Merchant SKU") > -1 && <MenuItem onClick={showSkuMatchDialog}>Match SKUs</MenuItem>}
                                    {!state.editable && metadata.Status !== 1200 && metadata.Status !== 1400 && metadata.CoId !== metadata.PartnerCoId && props.isBillable && props.isRetailer && <MenuItem onClick={() => setState({editable: true})}>Edit</MenuItem>}
                        </MoreActions>
                        {metadata.Status < 1000 && metadata.Status > 0 && <Button variant='outlined' component={Link} to={`/order-management/ack-details?orderid=${metadata.Id}`}>Accept/Reject</Button>}
                        {metadata.Status < 1000 && metadata.Status > 0 && <Button variant='outlined' component={Link} to={`/order-management/shipment-details?orderid=${metadata.Id}`}>Ship</Button>}
                        {metadata.Status < 1000 && metadata.Status > 0 && <Button variant='outlined' component={Link} to={`/order-management/invoice-details?orderid=${metadata.Id}`}>Invoice</Button>}
                        {state.editable && <DownloadButton loading={state.submitting} variant='contained' color='primary' onClick={() => triggerSubmit(formRef.current)}>Save Changes</DownloadButton>}
                    </DocumentToolbar>
                </Grid>
                    <Grid size={12}>
                        <OrderDetailsForm order={state.doc} metadata={state.metadata} disabled={!state.editable || state.submitting} handleSubmit={handleSubmit} setForm={form => formRef.current = form}/>
                    </Grid>
                    <Grid size={12}>
                        <DocumentTables showPosts={true} metadata={state.metadata} refresh={state.refresh} signalr={props.signalr} onEdiExists={e => setState({ediExists: e})} onEventLoad={(data) => {
                            if(data && data.length > 0 && data[0].EventLevel === 'Alert'){
                                setState({mostRecentEvent: data[0]});
                            }
                        }}
                        onPostLoad={posts => setState({mostRecentPost: posts[0]})}
                        />
                    </Grid>
                </Grid>
                <LabelDialog open={state.showPackingSlip} onClose={() => setState({showPackingSlip: false})} fileName={`${metadata.Id}-picklist`} path={`/api/v1/orders/picklist?logicbrokerkeys=${metadata.Id}`}/>
            </>
           : <div></div>}
            <ConfirmDialog open={state.showConfirm} onConfirm={state.handleConfirm} onClose={() => setState({showConfirm: false})} message={state.confirmMessage}></ConfirmDialog>
            <Dialog open={state.showError} onClose={() => {setState({showError: false})}}>
                <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                <DialogContent><Typography>{state.error}</Typography></DialogContent>
            </Dialog>
            <Dialog
                open={state.showRedact}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableRedact) return;
                  }
                  setState({showRedact: false});
                }}>
               <Toolbar className='lbtoolbar'>{'Remove Personal Data'}</Toolbar>
               <DialogContent>
                 <Typography>{'WARNING: This will remove address information from any document linked to this one and the content of all attached files for every affected document will be removed.'}</Typography>
                 <Typography style={{marginTop: '1rem'}}>Remove address information for:</Typography>
                 <RadioGroup value={state.selectedAddress} onChange={(e) => setState({selectedAddress: e.target.value})}>
                     {state.addresses && state.addresses.map(r =>  <FormControlLabel key={r.label} value={r.label} control={<Radio />} label={r.label}/>)}
                 </RadioGroup>
               </DialogContent>
               <DialogActions>
                 <Button variant='text' disabled={state.disableRedact} onClick={() => setState({showRedact: false})}>Cancel</Button>
                 <Button variant='text' disabled={state.disableRedact} color='primary' onClick={() => removePersonalData()}>Remove Information</Button>
               </DialogActions>
            </Dialog><Dialog
                maxWidth='md'
                fullWidth={true}
                open={state.showSkuMatch}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableSkuMatch) return;
                  }
                  setState({showSkuMatch: false});
                }}>
              <Toolbar className='lbtoolbar'>{'Fix SKU Mappings'}</Toolbar>
              <Typography style={{padding: '10px 24px 5px 24px'}}>{'Configure mappings for unmatched SKUs below. All new mappings will be added to the matching file and will apply to future orders and any orders that are reprocessed.'}</Typography>
              <Form initialValues={state.skusToMatch} onSubmit={saveSkuMatches} mutators={{...arrayMutators}} render={({ handleSubmit, pristine, reset, submitting, values }) => {
                    return (<Fragment>
              <DialogContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Line</TableCell>
                            <TableCell>Merchant SKU</TableCell>
                            <TableCell>{props.isRetailer ? 'Supplier' : 'Merchant'}</TableCell>
                            <TableCell>Supplier SKU</TableCell>
                            <TableCell>Cost</TableCell>
                        </TableRow>
                        </TableHead>
                <TableBody>
                          <FieldArray name="items">
                          {({fields}) =>
                              fields.map((name, index) => <TableRow key={name}>
                              <TableCell>{fields.value[index].LineNumber}</TableCell>
                              <TableCell>{fields.value[index].MerchantSKU}</TableCell>
                              <TableCell><Field disabled={state.disableSkuMatch || fields.value[index].matched || fields.value[index].disablePartner} component={AutoComplete} name={`${name}.PartnerId`} options={state.skuPartners}/></TableCell>
                              <TableCell><Field disabled={state.disableSkuMatch || fields.value[index].matched} component={TextField} name={`${name}.SupplierSKU`}/></TableCell>
                              <TableCell><Field disabled={state.disableSkuMatch || fields.value[index].matched} component={TextField} name={`${name}.Cost`} currency  currencyCode={state.doc.Currency}/></TableCell>
                              </TableRow>)
                          }
                          </FieldArray>
    </TableBody></Table>
              </DialogContent>
              <DialogActions>
                <Button variant='text' disabled={state.disableSkuMatch} onClick={() => setState({showSkuMatch: false})}>Cancel</Button>
                <Button variant='text' disabled={state.disableSkuMatch || (values.items || []).filter(r => !r.PartnerId || !r.SupplierSKU).length > 0} color='primary' onClick={handleSubmit}>Save and reprocess</Button>
              </DialogActions>
               </Fragment>);}}/>
           </Dialog>
            <Dialog
                maxWidth='md'
                fullWidth={true}
                open={state.showReroute}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (state.disableReroute) return;
                  }
                  setState({showReroute: false});
                }}>
               <Toolbar className='lbtoolbar'>Reroute Items</Toolbar>
               <Form initialValues={state.orderRouting} onSubmit={rerouteOrder} mutators={{...arrayMutators}} render={({ handleSubmit, pristine, reset, submitting, values }) => {
                     return (<Fragment>
               <DialogContent>
                 <Table>
                     <TableHead>
                         <TableRow>
                             <TableCell style={{padding: 0}}>Item</TableCell>
                             <TableCell style={{width: '14rem', padding: 0}}>Status</TableCell>
                             <TableCell style={{width: '14rem', padding: 0}}>Current Supplier</TableCell>
                             <TableCell style={{width: '14rem', padding: 0}}>New Supplier</TableCell>
                         </TableRow>
                         </TableHead>
                 <TableBody>
                           <FieldArray name="items">
                           {({fields}) =>
                               fields.map((name, index) => <TableRow key={name}>
                               <TableCell style={{padding: 0}}>
                                 <Typography>{fields.value[index].ItemIdentifier.SupplierSKU}, QTY: {fields.value[index].Quantity}</Typography>
                                 <Typography color="textSecondary">{fields.value[index].Description}</Typography>
                               </TableCell>
                               <TableCell style={{padding: 0}}>{<Status description={fields.value[index].po.statusDescription}/>}</TableCell>
                               <TableCell style={{padding: 0}}>{fields.value[index].po.partnerCompanyName}</TableCell>
                               <TableCell style={{padding: 0}}><Field disabled={state.disableReroute || (fields.value[index].po.status > 500 && fields.value[index].po.status <= 1000)} component={AutoComplete} name={`${name}.NewPartnerId`} options={state.skuPartners}/></TableCell>
                               </TableRow>)
                           }
                           </FieldArray>
     </TableBody></Table>
               </DialogContent>
               <DialogActions>
                 <Button variant='text' disabled={state.disableReroute} onClick={() => setState({showReroute: false})}>Cancel</Button>
                 <Button variant='text' disabled={state.disableReroute || (values.items || []).filter(r => !(!r.NewPartnerId)).length === 0} color='primary' onClick={handleSubmit}>Save and reprocess</Button>
               </DialogActions>
                </Fragment>);}}/>
            </Dialog>
        </div>
    );
};

const calculator = createDecorator(
  {
    field: /OrderLines\[\d+\].Price/,
    updates: (currentValue: any, name: string, allValues: any, prevValues: any) => {
        if(getIn(prevValues, name) === currentValue || getIn(prevValues, name) === undefined){
            return {};
        }
        let subtotal = (allValues.OrderLines || []).reduce((sum, value) => sum + (Number(value.Price || 0) * Number(value.Quantity || 0)), 0);
        let total = subtotal - Number(allValues.discountTotal || 0) + Number(allValues.taxTotal || 0) + Number(allValues.HandlingAmount || 0) + Number(allValues.DropshipAmount || 0);
        return {
            subtotal: parseFloat(subtotal.toFixed(2)),
            TotalAmount: parseFloat(total.toFixed(2))
        };
    }
}
)

interface OrderDetailsFormProps extends DocumentFormProps {
    order: any;
}

const OrderDetailsForm = (props: OrderDetailsFormProps) => {
    let {disabled} = props;
    let failedOnly = disabled || (props.metadata || {}).Status !== 1200;
    let currencyCode = props.order.Currency;
    return (
    <Form initialValues={props.order} onSubmit={props.handleSubmit} decorators={disabled ? null : [calculator]} mutators={{...arrayMutators, updateAddress}} render={({ handleSubmit, pristine, reset, submitting, values, form }) => {
          return (
              <form onSubmit={handleSubmit} ref={f => props.setForm(f)}><Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} style={{display: 'flex'}}>
                    <Address disabled={failedOnly} mutators={form.mutators} contact={props.order.BillToAddress} name='BillToAddress' label='Billing Address' style={{flex: 1}}/>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} style={{display: 'flex'}}>
                    <Address disabled={failedOnly} mutators={form.mutators} contact={props.order.ShipToAddress} name='ShipToAddress' label='Shipping Address' style={{flex: 1}}/>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
                        <Card style={{flex: 1}}>
                            <Toolbar className='lbtoolbar'>Shipping &amp; Payment Information</Toolbar>
                            <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                            <Field disabled={failedOnly} component={TextField} label='Shipping Method' name='ShipmentInfos.0.ClassCode'/>
                            <Field disabled={failedOnly} component={TextField} label='Service Level' name='ShipmentInfos.0.ServiceLevelDescription'/>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} style={{display: 'flex', paddingTop: 0, paddingBottom: 0}}>
                                  <Field style={{width: '100%'}} disabled={failedOnly} component={TextField} type='date' label='Requested Ship Date' name='RequestedShipDate'/>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }} style={{display: 'flex', paddingTop: 0, paddingBottom: 0}}>
                                  <Field style={{width: '100%'}} disabled={failedOnly} component={TextField} type='date' label='Expected Delivery Date' name='ExpectedDeliveryDate'/>
                              </Grid>
                            </Grid>
                            <Field disabled={failedOnly} component={TextField} label='Payment Terms' name='PaymentTerm.TermsDescription'/>
                            </CardContent>
                        </Card>
                    </Grid>
                <Grid size={12}>
                <Card>
                <Toolbar className='lbtoolbar'>Order Items</Toolbar>
                    <Table className='itemTable'>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{width: '5em'}}>Line</TableCell>
                                <TableCell style={{width: '12em'}}>SKU</TableCell>
                                <TableCell style={{width: '12em'}}>Partner SKU</TableCell>
                                <TableCell style={{width: '12em'}}>UPC</TableCell>
                                <TableCell style={{width: '7em'}}>Quantity</TableCell>
                                <TableCell style={{width: '7em'}}>Quantity Shipped</TableCell>
                                <TableCell style={{width: '7em'}}>Quantity Cancelled</TableCell>
                                <TableCell style={{width: '7em'}}>Price</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        <FieldArray name={`OrderLines`}>
                        {({fields}) =>
                            fields.map((name, index) => <Fragment key={name}><TableRow>
                            <TableCell>{fields.value[index].LineNumber}</TableCell>
                            <TableCell>{failedOnly ? fields.value[index].ItemIdentifier.SupplierSKU : <Field component={TextField} name={`${name}.ItemIdentifier.SupplierSKU`}/>}</TableCell>
                            <TableCell>{failedOnly ? fields.value[index].ItemIdentifier.PartnerSKU : <Field component={TextField} name={`${name}.ItemIdentifier.PartnerSKU`}/>}</TableCell>
                            <TableCell>{failedOnly ? fields.value[index].ItemIdentifier.UPC : <Field component={TextField} name={`${name}.ItemIdentifier.UPC`}/>}</TableCell>
                            <TableCell>{fields.value[index].Quantity}</TableCell>
                            <TableCell>{fields.value[index].quantityShipped}</TableCell>
                            <TableCell>{fields.value[index].quantityCancelled}</TableCell>
                            <TableCell><Field disabled={disabled} component={TextField} name={`${name}.Price`} currency currencyCode={currencyCode}/></TableCell>
                            <TableCell>{fields.value[index].Description}</TableCell>
                            </TableRow>
                            <ItemAttributes name={name} disabled={failedOnly} item={fields.value[index]}/>
                            </Fragment>)
                        }
                        </FieldArray>
                        </TableBody>
                    </Table>
                    </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
                        <Card style={{flex: 1}}>
                              <Toolbar className='lbtoolbar'>Notes</Toolbar>
                              <CardContent style={{display: 'flex', flexDirection: 'column', minHeight: '5em', wordBreak: 'break-word', textAlign: 'left', whiteSpace: 'pre-wrap'}}>
                              {props.order.Note}
                              </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
                        <Card style={{flex: 1}}>
                              <Toolbar className='lbtoolbar'>General Information</Toolbar>
                              <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                              <Field disabled component={TextField} label='Order ID' name='OrderNumber'/>
                              <Field disabled={failedOnly} component={TextField} label='Partner Order ID' name='ERPKey'/>
                              <Field disabled component={TextField} label='Reference Number' name='PartnerPO'/>
                              <HeaderAttributes disabled={failedOnly}/>
                              </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
                        <Card style={{flex: 1}}>
                              <Toolbar className='lbtoolbar'>Order Totals</Toolbar>
                              <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                              <Field disabled component={TextField} label='Subtotal' name='subtotal' currency currencyCode={currencyCode}/>
                              <Field disabled component={TextField} label='Discount' name='discountTotal' currency currencyCode={currencyCode}/>
                              <Field disabled component={TextField} label='Shipping/Freight' name='HandlingAmount' currency currencyCode={currencyCode}/>
                              <Field disabled component={TextField} label='Dropship Fee' name='DropshipAmount' currency currencyCode={currencyCode}/>
                              <Field disabled component={TextField} label='Tax' name='taxTotal' currency currencyCode={currencyCode}/>
                              <Field disabled component={TextField} label='Order Total' name='TotalAmount' currency currencyCode={currencyCode}/>
                              </CardContent>
                        </Card>
                    </Grid>
                </Grid></form>
      );}}/>
  );
};

export default OrderDetails;
